import type { OrderResponse } from '@/lib/api/schemas';
import { useConnectivityStore } from '@/stores/connectivity-store';

export type IncomingEvent =
  | { type: 'order.print_succeeded'; orderId: string }
  | { type: 'order.print_failed'; orderId: string; reason: string }
  | { type: 'order.expired'; orderId: string }
  | { type: 'menu.updated'; version: string }
  | { type: 'kiosk.config_updated'; config: Record<string, unknown> };

export type OutgoingEvent =
  | { type: 'subscribe'; channel: `order:${string}` }
  | { type: 'heartbeat'; timestamp: number };

type Listener = (event: IncomingEvent) => void;

const HEARTBEAT_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * Native-WebSocket client with heartbeat, exponential-backoff reconnect, and
 * per-screen re-subscription. MSW can't intercept WebSocket, so in mock mode we
 * skip the real socket and surface events via the MockWebSocket driver instead.
 */
export class WsClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private subscriptions = new Set<string>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private closed = false;

  constructor(private url: string) {}

  connect(): void {
    this.closed = false;
    this.open();
  }

  private open(): void {
    try {
      this.socket = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket.onopen = () => {
      this.attempt = 0;
      useConnectivityStore.getState().setWsConnected(true);
      this.subscriptions.forEach((channel) =>
        this.send({ type: 'subscribe', channel: channel as `order:${string}` }),
      );
      this.heartbeatTimer = setInterval(
        () => this.send({ type: 'heartbeat', timestamp: Date.now() }),
        HEARTBEAT_MS,
      );
    };

    this.socket.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data) as IncomingEvent;
        this.listeners.forEach((l) => l(event));
      } catch {
        // ignore malformed frames
      }
    };

    this.socket.onclose = () => {
      useConnectivityStore.getState().setWsConnected(false);
      this.clearHeartbeat();
      if (!this.closed) this.scheduleReconnect();
    };

    this.socket.onerror = () => this.socket?.close();
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  send(event: OutgoingEvent): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(event));
    }
  }

  subscribe(channel: `order:${string}`): void {
    this.subscriptions.add(channel);
    this.send({ type: 'subscribe', channel });
  }

  unsubscribeAll(): void {
    this.subscriptions.clear();
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.closed = true;
    this.clearHeartbeat();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
  }
}

/**
 * Mock WS used in mock mode and tests: drives a scripted timeline of events
 * instead of a real socket. Implements the slice of WsClient that the app uses.
 */
export class MockWsClient {
  private listeners = new Set<Listener>();

  connect(): void {
    useConnectivityStore.getState().setWsConnected(true);
  }

  send(): void {}
  subscribe(_channel: `order:${string}`): void {}
  unsubscribeAll(): void {}

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Test/mock helper: push an event to all listeners. */
  emit(event: IncomingEvent): void {
    this.listeners.forEach((l) => l(event));
  }

  close(): void {
    useConnectivityStore.getState().setWsConnected(false);
  }
}

export type AnyWsClient = WsClient | MockWsClient;

let instance: AnyWsClient | null = null;

/** App-wide WS client. Lazy singleton so screens can subscribe to the same connection main.tsx opened. */
export function getWsClient(): AnyWsClient {
  if (!instance) {
    // Same runtime gate as the HTTP mocks in main.tsx: __USE_MOCKS__ is always
    // true in dev builds, so VITE_USE_MOCKS=false must win to reach the real WS.
    // Vitest (mode "test") always gets the mock so specs can emit events.
    const useMock =
      (__USE_MOCKS__ && import.meta.env.VITE_USE_MOCKS !== 'false') ||
      import.meta.env.MODE === 'test';
    instance = useMock
      ? new MockWsClient()
      : new WsClient(
          `${import.meta.env.VITE_WS_URL}/kiosk/${import.meta.env.VITE_KIOSK_ID}?kiosk_token=${import.meta.env.VITE_KIOSK_TOKEN_BOOTSTRAP}`,
        );
  }
  return instance;
}

export type { OrderResponse };
